import { useEffect } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { hasModule, firstAllowedRoute } from "@/lib/auth";
import { ArticlesClient } from "@/features/warehouse/ArticlesClient";
import { getOwner, listOwners, warehouseKeys } from "@/features/warehouse/warehouse.api";

// Warehouse → Artikelen (docs/design_handoff_wms/README.md §4.2). The owner and the article ride
// in the address, so a link opens the same article. Reading is wms_masterdata; adding and filling
// in articles is wms_masterdata_write.
interface ArticlesSearch {
  owner?: string;
  article?: string;
}

export const Route = createFileRoute("/_shell/warehouse/articles")({
  beforeLoad: ({ context }) => {
    if (!hasModule(context.session, "wms_masterdata") && !hasModule(context.session, "wms_masterdata_write")) {
      throw redirect({ to: firstAllowedRoute(context.session) });
    }
  },
  validateSearch: (search: Record<string, unknown>): ArticlesSearch => {
    const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
    return { owner: str(search.owner), article: str(search.article) };
  },
  component: ArticlesScreen,
});

function ArticlesScreen() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { session } = Route.useRouteContext();
  const canEdit = session.roles.includes("admin") || hasModule(session, "wms_masterdata_write");

  // Without an owner in the address: the first one WLX knows (with articles first).
  const known = useQuery({ queryKey: warehouseKeys.owners(""), queryFn: () => listOwners(), enabled: !search.owner });
  useEffect(() => {
    const first = known.data?.[0];
    if (!search.owner && first) navigate({ search: { owner: first.id }, replace: true });
  }, [search.owner, known.data, navigate]);

  const owner = useQuery({ queryKey: warehouseKeys.owner(search.owner ?? ""), queryFn: () => getOwner(search.owner!), enabled: !!search.owner });

  return (
    <div className="wl-page" data-screen-label="Back-office Warehouse Articles">
      <ArticlesClient
        owner={owner.data}
        articleId={search.article}
        canEdit={canEdit}
        isAdmin={session.roles.includes("admin")}
        onChooseOwner={(next) => navigate({ search: { owner: next.id } })}
        onOpenArticle={(id) => navigate({ search: (prev) => ({ ...prev, article: id }), replace: true })}
      />
    </div>
  );
}
